/**
 * Section B7.3 Service - B.7.3.洩漏排放處理
 * Leakage emissions handling
 *
 * 架構說明：
 * - leakage_sources / leakage_handling：PUT /{project_id} 儲存
 * - leakage_calculate：每筆 LeakageEntry 含 3 個精靈階段資料
 */
import axios from '../api/axios';

const BASE_URL = '/api/section-b7-3';

// ── leakage_calculate JSON 子結構 ─────────────────────────────────

export interface LeakageParameter {
  symbol: string;
  para_type?: number | null;    // system_codes.id, null=皆適用
  scenarios: string;             // HTML 富文本
  unit: string;                  // LaTeX 語法
  annual_value?: number | null;  // 保守估算 (10,2)
}

export interface LeakageEntry {
  id: string;                              // UUID v4
  methodology: string;                     // 適用方法論名稱
  equation: { latex: string };             // LaTeX 公式
  equation_note: string;                   // 富文本，inline toolbar
  parameters: LeakageParameter[];
  calculation_result: { value: number | null; unit: string };
}

// ── 主資料表 ──────────────────────────────────────────────────────

export interface SectionB73Detail {
  id: number;
  organization_id: number;
  project_id: number;
  leakage_sources: string | null;
  leakage_handling: string | null;
  leakage_calculate: LeakageEntry[] | null;
  b7_3: number;
  edit_by: number;
  created_at: string;
  updated_at: string | null;
}

// ── Request Payloads ──────────────────────────────────────────────

export interface SectionB73Update {
  leakage_sources?: string | null;
  leakage_handling?: string | null;
}

export interface LeakageEntryCreate {
  methodology: string;
  equation: { latex: string };
  equation_note: string;
  parameters: LeakageParameter[];
  calculation_result: { value: number | null; unit: string };
}

export interface LeakageEntryUpdate {
  methodology?: string;
  equation?: { latex: string };
  equation_note?: string;
  parameters?: LeakageParameter[];
  calculation_result?: { value: number | null; unit: string };
}

// ── API 函式 ──────────────────────────────────────────────────────

export const getSectionB73ByProject = async (projectId: number): Promise<SectionB73Detail | null> => {
  const response = await axios.get<SectionB73Detail>(`${BASE_URL}/by-project/${projectId}`);
  return response.data;
};

export const updateSectionB73 = async (projectId: number, data: SectionB73Update): Promise<SectionB73Detail> => {
  const response = await axios.put<SectionB73Detail>(`${BASE_URL}/${projectId}`, data);
  return response.data;
};

export const addLeakageEntry = async (projectId: number, entry: LeakageEntryCreate): Promise<SectionB73Detail> => {
  const response = await axios.post<SectionB73Detail>(`${BASE_URL}/${projectId}/leakage`, entry);
  return response.data;
};

export const updateLeakageEntry = async (
  projectId: number,
  entryId: string,
  entry: LeakageEntryUpdate
): Promise<SectionB73Detail> => {
  const response = await axios.put<SectionB73Detail>(`${BASE_URL}/${projectId}/leakage/${entryId}`, entry);
  return response.data;
};

export const deleteLeakageEntry = async (projectId: number, entryId: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/${projectId}/leakage/${entryId}`);
};

export const resetSectionB73 = async (projectId: number): Promise<void> => {
  await axios.post(`${BASE_URL}/${projectId}/reset`);
};

const sectionB73Service = {
  getSectionB73ByProject,
  updateSectionB73,
  addLeakageEntry,
  updateLeakageEntry,
  deleteLeakageEntry,
  resetSectionB73,
};
export default sectionB73Service;
